import { Vector3 } from '@babylonjs/core/Maths/math.vector'
import { TransformNode } from '@babylonjs/core/Meshes/transformNode'
import { Tools } from '@babylonjs/core/Misc/tools'
import { Scene } from '@babylonjs/core/scene'
import { distinct } from '@lindberg/utils'
import getMaterial from './MaterialLoader'
import Bridge from './Parts/Bridge'
import Bushing from './Parts/Bushing'
import Clip from './Parts/Clip'
import Front from './Parts/Front'
import Lens from './Parts/Lens'
import Nosepad from './Parts/Nosepad'
import Part from './Parts/Part'
import Temple from './Parts/Temple'
import { GetUrl, Settings } from './Settings'
export type ComponentType =
	| 'MODEL'
	| 'BRIDGE'
	| 'BUSHING'
	| 'CLIP'
	| 'COVER'
	| 'CROSSBAR'
	| 'FRONT'
	| 'GEMSTONE'
	| 'GROOVE'
	| 'INNERRIM'
	| 'LENSES'
	| 'LOWERRIM'
	| 'NOSEPAD'
	| 'NOSEPADARM'
	| 'SNAP_ON'
	| 'TEMPLE'
	| 'TEMPLE_ACETATE'
	| 'TEMPLE_CLIP'
	| 'TEMPLE_DETAIL'
	| 'TEMPLE_HORN'
	| 'TEMPLE_TIP'
	| 'UPPERRIM'
	| 'WIRE'
	| 'LENS_EDGE'
	| 'UPPERRIM_DETAIL'
export type Productline = 'SPI' | '8000' | 'AIR' | '1000' | 'STRIP' | '6500' | 'TT' | '1800' | 'RIM' | 'MØF' | '9200' | '9500' | '7000' | 'RIMKID' | 'RIMTEEN' | '1000TEEN'
export type ComponentConfiguration = { design?: string; size?: string; color?: string }

export type FrameConfiguration = {
	productline: Productline
	isRimless: boolean

	inclination?: number
	closedTemples?: boolean
	closingAngles?: number[]

	engraving?: string

	allowErrors?: boolean
	disableEngraving?: boolean
	requiredMaterials?: string[]

	components: {
		[P in ComponentType]?: ComponentConfiguration
	}
}

class Pair<T extends Part> extends Array<T> {
	constructor(right: T, left: T) {
		super(2)
		this[0] = right
		this[1] = left
	}

	get right(): T {
		return this[0]
	}
	get left(): T {
		return this[1]
	}

	set config(val: FrameConfiguration) {
		this.right.config = val
		this.left.config = val
	}

	get hasErrors(): boolean {
		return this.right.hasErrors || this.left.hasErrors
	}
	get errors(): string[] {
		return Array.from(new Set([...this.right.errors, ...this.left.errors]))
	}

	async load() {
		return Promise.allSettled([
			this.right.load(),
			this.left.load()
		])
	}

	get filename() {
		return this.left.filename
	}

	swap(animated: boolean = false) {
		this.right.swap(animated)
		this.left.swap(animated)
	}

	assignMaterials() {
		this.right.assignMaterials()
		this.left.assignMaterials()
	}

	setEnabled(value: boolean) {
		this.right.setEnabled(value)
		this.left.setEnabled(value)
	}

	async compileMaterials() {
		return Promise.allSettled([
			this.right.compileMaterials(),
			this.left.compileMaterials()
		])
	}
}

export function GetConfigurationString(configuration: FrameConfiguration): string {
	const config = configuration ?
		new URLSearchParams(Object.fromEntries([
			...Object.keys(configuration).filter(x => x != 'components').map(x => [x, configuration[x]?.toString()]),
			...Object.keys(configuration.components).map(x => [x, ['design', 'size', 'color'].map(y => configuration.components[x][y]?.replace(' ', '_').replace('+', '_') || '').join(' ')])
		])).toString() :
		''
	return config.replaceAll('%23', '□').replaceAll('%2F', '/')
}

export function GetConfigurationFromString(value: string): FrameConfiguration {
	const params = new URLSearchParams(value)
	if (!params.get('MODEL')) return
	const properties = Array.from(params).filter(x => x[0] != x[0].toUpperCase())
	const components = Array.from(params).filter(x => x[0] == x[0].toUpperCase()).map(x => [x[0], Object.fromEntries(x[1].split(' ').map((v, i) => [['design', 'size', 'color'][i], v]))])

	const configuration = Object.fromEntries([...properties, ['components', Object.fromEntries(components)]]) as FrameConfiguration
	configuration.isRimless = params.get('isRimless') == 'true'

	return configuration
}

export class Frame extends TransformNode {
	parts: {
		front?: Front
		bridge?: Bridge
		lenses?: Pair<Lens>
		clips?: Pair<Clip>
		temples?: Pair<Temple>
		nosepads?: Pair<Nosepad>
		bushings?: Pair<Bushing>
	} = {}

	public configuration: FrameConfiguration
	public hasErrors: boolean = false

	public center: Vector3
	public size: Vector3

	get errors() {
		return Array.from(new Set(Object.entries(this.parts).filter(([, value]) => value.hasErrors).map(([, value]) => value.errors).flat()))
	}

	constructor(scene: Scene, configuration: FrameConfiguration = null) {
		super('FRAME', scene)
		if (configuration) this.configuration = configuration
	}

	buildFrame() {
		if (this.configuration.isRimless) {
			this.parts.bridge = new Bridge(this)
			this.parts.lenses = new Pair(new Lens(this.parts.bridge.LensOutRight), new Lens(this.parts.bridge.LensOutLeft, true))
			this.parts.nosepads = new Pair(new Nosepad(this.parts.bridge.NosepadOutRight), new Nosepad(this.parts.bridge.NosepadOutLeft))
			this.parts.clips = new Pair(new Clip(this.parts.lenses.right.ClipOut, true), new Clip(this.parts.lenses.left.ClipOut))
			this.parts.temples = new Pair(new Temple(this.parts.clips.right.TempleOut, true), new Temple(this.parts.clips.left.TempleOut, false))
			this.parts.bushings = new Pair(new Bushing(this.parts.clips.right.TempleOut, true), new Bushing(this.parts.clips.left.TempleOut, false))

			this.parts.bridge.rotation.x = -Tools.ToRadians(this.configuration.inclination ?? 10)
		} else {
			this.parts.front = new Front(this)
			this.parts.nosepads = new Pair(new Nosepad(this.parts.front.NosepadOutRight), new Nosepad(this.parts.front.NosepadOutLeft))
			this.parts.temples = new Pair(new Temple(this.parts.front.TempleOutRight, true), new Temple(this.parts.front.TempleOutLeft, false))
			this.parts.bushings = new Pair(new Bushing(this.parts.front.TempleOutRight, true), new Bushing(this.parts.front.TempleOutLeft, false))
		}
	}

	async load(animate: boolean = false) {
		const t = performance.now()
		let rebuild = false
		if ((this.configuration.isRimless && !this.parts.bridge) || (!this.configuration.isRimless && !this.parts.front)) {
			this.parts.front?.dispose()
			this.parts.bridge?.dispose()
			this.parts = {}
			this.buildFrame()
			animate = false
		}
		const loads: Promise<any>[] = []

		// Preload all materials
		const materialsToLoad = Object.keys(this.configuration.components).map(x => this.configuration.components[x] as ComponentConfiguration).filter(x => x.color).map(x => x.color)
		if (this.configuration.requiredMaterials) materialsToLoad.push(...this.configuration.requiredMaterials)
		loads.push(...distinct(materialsToLoad).map(x => getMaterial(this.getScene(), x, this.configuration.allowErrors)))

		// console.log(distinct(materialsToLoad))

		for (const str in this.parts) {
			const p: Part | Pair<Part> = this.parts[str]
			p.config = this.configuration
		}

		if (Settings.cloneParts) {
			const singleParts = Object.keys(this.parts).map(x => this.parts[x]).filter(x => x instanceof Part) as Part[]
			loads.push(...singleParts.map(x => x.load()))

			for (const pair of Object.keys(this.parts).map(x => this.parts[x]).filter(x => x instanceof Array) as Pair<Part>[]) {
				loads.push(
					new Promise<void>(async (resolve, reject) => {
						const url = GetUrl(pair.left.filename + '.glb')
						if (!pair.left.filename) return resolve()
						const request = await fetch(url, { headers: { 'Accept': 'model/gltf-binary' } })
						if (!request.ok) reject()
						const blob = await request.blob()
						await Promise.all([pair.left.load(new File([blob], pair.left.filename + '.glb')), pair.right.load(new File([blob], pair.left.filename + '.glb'))])
						resolve()
					})
				)
			}
		} else {
			loads.push(...Object.keys(this.parts).map(x => this.parts[x].load()))
		}
		const results = await Promise.allSettled(loads)
		await this.getScene().whenReadyAsync()
		this.hasErrors = Object.keys(this.parts).findIndex(x => this.parts[x].hasErrors) > -1
		if (results.find(x => x.status == 'rejected')) {
			this.hasErrors = true
			console.log(results.filter(x => x.status == 'rejected'))
		}
		const posYFrom = this.parts.bridge?.position.y || 0
		const rotXFrom = this.parts.bridge?.rotation.x || 0

		const { min, max } = this.getHierarchyBoundingVectors(true)
		this.center = min.add(max).scale(0.5)
		this.size = max.subtract(min)
		if (this.configuration.isRimless && this.parts.bridge) {
			this.parts.bridge.rotation.x = Tools.ToRadians(this.configuration.inclination ?? 10)
		}

		for (const str in this.parts) {
			await this.parts[str].compileMaterials()
		}

		for (const str in this.parts) {
			const part = this.parts[str].left || this.parts[str]
			if (part.filename) {
				this.parts[str].setEnabled(true)
				this.parts[str].swap(animate)
				this.parts[str].assignMaterials()
			} else {
				this.parts[str].setEnabled(false)
			}
		}

		if (this.configuration.isRimless && this.parts.bridge) {
			this.parts.bridge.position = Vector3.Zero()
			this.parts.bridge.position = this.getCenter().negate()
		} else if (this.parts.front) {
			this.parts.front.position = Vector3.Zero()
			this.parts.front.position = this.getCenter().negate()
		}
	}

	getCenter() {
		const bounds = this.getHierarchyBoundingVectors(true, mesh => mesh.name.startsWith('LENS') /* && mesh.name.endsWith("LEFT")*/)
		return bounds.max.add(bounds.min).scale(0.5)
	}
}
